import { useMemo, useRef, useState, type PointerEvent } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import type { RingMetric } from "./Screen0Ring";
import { formatActivationCountForDisplay, formatDuration } from "../../lib/format";

export { formatAveragedActivationCount } from "../../lib/format";

export type FocusSeriesPoint = { label: string; seconds: number; opens: number | null };

type ChartMode = "day" | "week" | "month";

type Props = {
  points: FocusSeriesPoint[];
  mode: ChartMode;
  metric: RingMetric;
  color: string;
  hasOpens: boolean;
  /** Habits daily-average chart: activations are means and need one-decimal formatting. */
  averagedActivations?: boolean;
};

// viewBox geometry. The chart scales responsively to its container width.
export const CHART_W = 300;
export const CHART_H = 150;
const W = CHART_W;
const H = CHART_H;

/** Axis labels match the active-metric indicator for readability. */
export const AXIS_LABEL_FONT_SIZE = 10;
export const METRIC_LABEL_FONT_SIZE = 10;

// Plot insets — tuned so fontSize-10 axis labels are fully visible at H=150.
export const PAD_L = 12;
export const PAD_R = 12;
export const PAD_T = 20;
export const PAD_B = 26;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;

/** Inset from the plot origin so Y tick labels sit inside the chart area and are not clipped. */
const Y_LABEL_INSET = 5;
const X_LABEL_Y = H - 8;

/** Maps a horizontal pointer position to the nearest series index inside the plot area. */
export function scrubIndexFromClientX(
  clientX: number,
  rectLeft: number,
  rectWidth: number,
  pointCount: number,
): number {
  if (pointCount <= 0) return 0;
  const ratio = rectWidth > 0 ? (clientX - rectLeft) / rectWidth : 0;
  const vbX = ratio * W;
  const i = Math.round(((vbX - PAD_L) / PLOT_W) * (pointCount - 1));
  return Math.min(pointCount - 1, Math.max(0, i));
}

export function shouldUpdateChartScrub(
  isScrubbing: boolean,
  buttons: number,
  pressure: number,
  pointerType: string,
): boolean {
  if (isScrubbing) return true;
  return pressure > 0 || buttons > 0 || pointerType === "mouse";
}

export function shouldClearChartScrubOnLeave(isScrubbing: boolean): boolean {
  return !isScrubbing;
}

const Y_AXIS_LABEL = "oklch(58% 0.018 155)";
const METRIC_INDICATOR = "oklch(62% 0.016 155)";

const SECONDARY_STROKE = "oklch(66% 0.02 155)";
const GRID_DOT = "oklch(40% 0.02 165)";

type Coord = { x: number; y: number };

const NICE_FRACTIONS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

/** Rounds peak × 1.2 up to a readable axis maximum (e.g. peak 10 → 12, peak 7 → 10). */
export function computeNiceAxisMax(peak: number): number {
  if (peak <= 0) return 1;
  const target = peak * 1.2;
  const exponent = Math.floor(Math.log10(target));
  const fraction = target / 10 ** exponent;
  const niceFraction = NICE_FRACTIONS.find((f) => f >= fraction) ?? 10;
  return niceFraction * 10 ** exponent;
}

/**
 * Maps a series to plot coordinates. When `yMax` is omitted, normalises to the
 * series max so screen-time and activations stay readable on independent scales.
 * When `yMax` is set (active metric), values map to a shared axis with headroom.
 */
export function buildSeriesCoords(values: number[], yMax?: number): Coord[] {
  const max = yMax ?? Math.max(...values, 1);
  const n = values.length;
  return values.map((value, i) => {
    const x = n <= 1 ? PAD_L + PLOT_W / 2 : PAD_L + (i / (n - 1)) * PLOT_W;
    const y = PAD_T + PLOT_H * (1 - Math.max(0, value) / max);
    return { x, y };
  });
}

/** Integer-friendly ticks for count metrics; duration metrics use fractional mid ticks. */
export function yAxisTicks(axisMax: number, integerTicks = false, peak = 0): number[] {
  if (integerTicks) {
    const labelMax = Math.max(1, Math.ceil(peak));
    if (labelMax === 1) return [0, 1];
    if (labelMax === 2) return [0, 1, 2];
    const mid = Math.ceil(labelMax / 2);
    return [...new Set([0, mid, labelMax])];
  }
  return [0, axisMax / 2, axisMax];
}

/** Drop ticks whose formatted label duplicates another (e.g. mid 0.6 → "1" when max → "1"). */
export function dedupeFormattedTicks(
  ticks: number[],
  format: (value: number) => string,
): number[] {
  const seen = new Set<string>();
  const result: number[] = [];
  for (const tick of ticks) {
    const label = format(tick);
    if (seen.has(label)) continue;
    seen.add(label);
    result.push(tick);
  }
  return result;
}

function formatAxisTick(
  value: number,
  metric: RingMetric,
  hasOpens: boolean,
  languageCode: ReturnType<typeof useScreen0Language>["languageCode"],
  averagedActivations: boolean,
): string {
  if (metric === "opens" && hasOpens) {
    return formatActivationCountForDisplay(value, averagedActivations ? "averaged" : "integer");
  }
  return formatDuration(Math.round(value), languageCode);
}

function activeMetricLabel(
  metric: RingMetric,
  hasOpens: boolean,
  t: ReturnType<typeof getTranslations>,
): string {
  if (metric === "opens" && hasOpens) return t.ring.activations;
  return t.ring.screenTime;
}

function smoothPath(coords: Coord[]): string {
  if (coords.length === 0) return "";
  if (coords.length === 1) return `M ${coords[0]!.x} ${coords[0]!.y}`;
  let d = `M ${coords[0]!.x} ${coords[0]!.y}`;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const p0 = coords[i - 1] ?? coords[i]!;
    const p1 = coords[i]!;
    const p2 = coords[i + 1]!;
    const p3 = coords[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function dayOfMonth(dateKey: string): string {
  const day = Number(dateKey.split("-")[2]);
  return Number.isFinite(day) ? String(day) : dateKey;
}

function xAxisLabels(points: FocusSeriesPoint[], mode: ChartMode): Array<{ x: number; text: string }> {
  const n = points.length;
  const xAt = (i: number) => (n <= 1 ? PAD_L + PLOT_W / 2 : PAD_L + (i / (n - 1)) * PLOT_W);
  if (mode === "week") {
    return points.map((point, i) => {
      const [y, m, d] = point.label.split("-").map(Number);
      const text = Number.isFinite(y) ? WEEKDAY[new Date(y!, (m ?? 1) - 1, d ?? 1).getDay()]! : point.label;
      return { x: xAt(i), text };
    });
  }
  if (mode === "month") {
    if (n === 0) return [];
    // Sparse day-of-month ticks so ~30 daily points stay readable.
    const indices = [...new Set([0, Math.round((n - 1) * 0.25), Math.round((n - 1) * 0.5), Math.round((n - 1) * 0.75), n - 1])];
    return indices.map((i) => ({ x: xAt(i), text: dayOfMonth(points[i]!.label) }));
  }
  return [0, 6, 12, 18, 23]
    .filter((i) => i < n)
    .map((i) => ({ x: xAt(i), text: points[i]!.label }));
}

export function FocusSeriesChart({
  points,
  mode,
  metric,
  color,
  hasOpens,
  averagedActivations = false,
}: Props) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const svgRef = useRef<SVGSVGElement>(null);
  const scrubbingRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const timeValues = useMemo(() => points.map((p) => p.seconds), [points]);
  const opensValues = useMemo(() => points.map((p) => p.opens ?? 0), [points]);

  const timeEmphasized = metric === "time" || !hasOpens;
  const opensEmphasized = metric === "opens" && hasOpens;

  const activeValues = opensEmphasized ? opensValues : timeValues;
  const activePeak = useMemo(() => Math.max(...activeValues, 0), [activeValues]);
  const axisMax = useMemo(() => computeNiceAxisMax(activePeak), [activePeak]);
  const yTicks = useMemo(() => {
    const raw = yAxisTicks(axisMax, opensEmphasized && !averagedActivations, activePeak);
    const format = (value: number) =>
      formatAxisTick(value, metric, hasOpens, languageCode, averagedActivations);
    return dedupeFormattedTicks(raw, format);
  }, [axisMax, opensEmphasized, averagedActivations, activePeak, metric, hasOpens, languageCode]);

  const timeCoords = useMemo(
    () => buildSeriesCoords(timeValues, timeEmphasized ? axisMax : undefined),
    [timeValues, timeEmphasized, axisMax],
  );
  const opensCoords = useMemo(
    () => buildSeriesCoords(opensValues, opensEmphasized ? axisMax : undefined),
    [opensValues, opensEmphasized, axisMax],
  );
  const timePath = useMemo(() => smoothPath(timeCoords), [timeCoords]);
  const opensPath = useMemo(() => smoothPath(opensCoords), [opensCoords]);
  const labels = useMemo(() => xAxisLabels(points, mode), [points, mode]);

  const drawTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.9, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] };

  const updateActive = (clientX: number) => {
    const svg = svgRef.current;
    if (!svg || points.length === 0) return;
    const rect = svg.getBoundingClientRect();
    setActiveIndex(scrubIndexFromClientX(clientX, rect.left, rect.width, points.length));
  };

  const beginScrub = (event: PointerEvent<SVGSVGElement>) => {
    // Capture phase + stopPropagation so Framer Motion pager drag (native bubble on
    // the ancestor) never starts when the press begins on the chart.
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    scrubbingRef.current = true;
    updateActive(event.clientX);
  };

  const moveScrub = (event: PointerEvent<SVGSVGElement>) => {
    if (!shouldUpdateChartScrub(scrubbingRef.current, event.buttons, event.pressure, event.pointerType)) {
      return;
    }
    if (scrubbingRef.current) event.stopPropagation();
    updateActive(event.clientX);
  };

  const endScrub = (event: PointerEvent<SVGSVGElement>) => {
    scrubbingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setActiveIndex(null);
  };

  const active = activeIndex != null ? points[activeIndex] : null;
  const activeX = activeIndex != null ? timeCoords[activeIndex]!.x : 0;
  const baselineY = PAD_T + PLOT_H;

  return (
    <div className="relative w-full">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full touch-none"
        style={{ display: "block" }}
        role="img"
        aria-label={
          mode === "day"
            ? t.ring.chartDescriptionDay
            : mode === "month"
              ? t.ring.chartDescriptionMonth
              : t.ring.chartDescriptionWeek
        }
        onPointerDownCapture={beginScrub}
        onPointerMove={moveScrub}
        onPointerLeave={() => {
          if (shouldClearChartScrubOnLeave(scrubbingRef.current)) setActiveIndex(null);
        }}
        onPointerUp={endScrub}
        onPointerCancel={endScrub}
      >
        <DotGrid />

        {/* Y-axis baseline at the plot origin */}
        <line
          x1={PAD_L}
          y1={PAD_T}
          x2={PAD_L}
          y2={baselineY}
          stroke={Y_AXIS_LABEL}
          strokeWidth={0.75}
          style={{ opacity: 0.35 }}
        />

        {/* Y-axis ticks for the active metric — labels inside the plot area */}
        {yTicks.map((tick) => {
          const atBaseline = tick === 0;
          const y = atBaseline ? baselineY - 2 : PAD_T + PLOT_H * (1 - tick / axisMax);
          return (
            <text
              key={tick}
              x={PAD_L + Y_LABEL_INSET}
              y={y}
              textAnchor="start"
              dominantBaseline={atBaseline ? "auto" : "middle"}
              fontSize={AXIS_LABEL_FONT_SIZE}
              fill={Y_AXIS_LABEL}
              className="mono"
            >
              {formatAxisTick(tick, metric, hasOpens, languageCode, averagedActivations)}
            </text>
          );
        })}

        {/* Active metric indicator */}
        <text
          x={W - PAD_R + 2}
          y={PAD_T + 6}
          textAnchor="end"
          fontSize={METRIC_LABEL_FONT_SIZE}
          fill={METRIC_INDICATOR}
          className="mono"
        >
          {activeMetricLabel(metric, hasOpens, t)}
        </text>

        {/* Activations line (or dashed zero baseline when not tracked) */}
        {hasOpens ? (
          <motion.path
            d={opensPath}
            fill="none"
            stroke={opensEmphasized ? color : SECONDARY_STROKE}
            strokeWidth={opensEmphasized ? 2.4 : 1.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ opacity: opensEmphasized ? 1 : 0.4 }}
            initial={{ pathLength: prefersReducedMotion ? 1 : 0 }}
            animate={{ pathLength: 1 }}
            transition={drawTransition}
          />
        ) : (
          <line
            x1={PAD_L}
            y1={baselineY}
            x2={W - PAD_R}
            y2={baselineY}
            stroke={SECONDARY_STROKE}
            strokeWidth={1.2}
            strokeDasharray="3 4"
            style={{ opacity: 0.45 }}
          />
        )}

        {/* Screen time line */}
        <motion.path
          d={timePath}
          fill="none"
          stroke={timeEmphasized ? color : SECONDARY_STROKE}
          strokeWidth={timeEmphasized ? 2.4 : 1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ opacity: timeEmphasized ? 1 : 0.4 }}
          initial={{ pathLength: prefersReducedMotion ? 1 : 0 }}
          animate={{ pathLength: 1 }}
          transition={drawTransition}
        />

        {/* Tooltip guide */}
        {active != null && activeIndex != null ? (
          <g>
            <line
              x1={activeX}
              y1={PAD_T}
              x2={activeX}
              y2={baselineY}
              stroke="oklch(80% 0.02 155)"
              strokeWidth={0.75}
              style={{ opacity: 0.4 }}
            />
            <circle cx={activeX} cy={timeCoords[activeIndex]!.y} r={2.6} fill={timeEmphasized ? color : SECONDARY_STROKE} />
            {hasOpens ? (
              <circle cx={activeX} cy={opensCoords[activeIndex]!.y} r={2.6} fill={opensEmphasized ? color : SECONDARY_STROKE} />
            ) : null}
          </g>
        ) : null}

        {/* X axis labels */}
        {labels.map((label) => (
          <text
            key={`${label.x}-${label.text}`}
            x={label.x}
            y={X_LABEL_Y}
            textAnchor="middle"
            fontSize={AXIS_LABEL_FONT_SIZE}
            fill={Y_AXIS_LABEL}
            className="mono"
          >
            {label.text}
          </text>
        ))}
      </svg>

      {active != null ? (
        <div
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 rounded-md px-2 py-1 text-[11px]"
          style={{
            background: "color-mix(in oklch, oklch(24% 0.022 165) 92%, black)",
            border: "1px solid oklch(34% 0.028 165)",
            color: "oklch(88% 0.018 150)",
            whiteSpace: "nowrap",
          }}
        >
          <span className="mono">{tooltipLabel(active, mode, languageCode, t, averagedActivations)}</span>
        </div>
      ) : null}
    </div>
  );
}

function tooltipLabel(
  point: FocusSeriesPoint,
  mode: ChartMode,
  languageCode: ReturnType<typeof useScreen0Language>["languageCode"],
  t: ReturnType<typeof getTranslations>,
  averagedActivations: boolean,
): string {
  const head = mode === "day"
    ? `${point.label}:00`
    : mode === "month"
      ? monthDayShort(point.label)
      : weekdayFull(point.label);
  const time = formatDuration(point.seconds, languageCode);
  const opensCount =
    point.opens != null
      ? formatActivationCountForDisplay(point.opens, averagedActivations ? "averaged" : "integer")
      : null;
  const opens =
    opensCount != null ? formatTemplate(t.ring.tooltipActAbbrev, { count: opensCount }) : "";
  return `${head} · ${time}${opens}`;
}

function monthDayShort(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  if (!Number.isFinite(y)) return dateKey;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(
    new Date(y!, (m ?? 1) - 1, d ?? 1),
  );
}

function weekdayFull(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  if (!Number.isFinite(y)) return dateKey;
  return WEEKDAY[new Date(y!, (m ?? 1) - 1, d ?? 1).getDay()]!;
}

function DotGrid() {
  const cols = 13;
  const rows = 5;
  const dots: Array<{ x: number; y: number }> = [];
  for (let c = 0; c < cols; c += 1) {
    for (let r = 0; r < rows; r += 1) {
      dots.push({
        x: PAD_L + (c / (cols - 1)) * PLOT_W,
        y: PAD_T + (r / (rows - 1)) * PLOT_H,
      });
    }
  }
  return (
    <g style={{ opacity: 0.5 }}>
      {dots.map((dot) => (
        <circle key={`${dot.x}-${dot.y}`} cx={dot.x} cy={dot.y} r={0.6} fill={GRID_DOT} />
      ))}
    </g>
  );
}
